import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Home, Send, Wallet, Fuel, ScanLine, LogOut, ChevronDown, Loader2, Truck, Monitor } from "lucide-react";
import { fmtCurrency, fmtDate } from "@/lib/format";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("tl_token")}`, "X-Company-Id": localStorage.getItem("tl_company") });
const today = () => new Date().toISOString().slice(0, 10);

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const ScanBox = ({ title, sub, loading, onFile, testid }) => (
  <label className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 px-4 py-4 active:scale-[0.99] transition-transform" data-testid={testid}>
    <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanLine className="h-5 w-5" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-bold">{loading ? "Reading document…" : title}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
    <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={onFile} disabled={loading} data-testid={`${testid}-input`} />
  </label>
);

function ChallanScreen({ isDalmia, vehicles, materials, loadLocs, delLocs, hub, onDone }) {
  const empty = { gr_date: today(), gr_no: "", vehicle_number: "", owner_name: "", vehicle_type: "Open", material_type: materials[0] || "Cement", load_location: loadLocs[0] || "", delivery_location: "", delivery_number: "", quantity: "", company_rate: "", diesel: "", bank_transfer: "", fuel_qty: "", unloading_amount: "" };
  const [f, setF] = useState(empty);
  const [ocr, setOcr] = useState(false);
  const [saving, setSaving] = useState(false);

  const pickVehicle = (vn) => {
    const v = vehicles.find((x) => x.vehicle_number === vn);
    setF((p) => ({ ...p, vehicle_number: vn, owner_name: v ? v.owner_name : p.owner_name, vehicle_type: v ? v.vehicle_type : p.vehicle_type }));
  };

  const scan = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setOcr(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/challans/invoice-ocr`, fd, { headers: authHeaders() });
      setF((p) => {
        const vn = data.vehicle_number || p.vehicle_number;
        const v = vehicles.find((x) => x.vehicle_number.toLowerCase() === (vn || "").trim().toLowerCase());
        return { ...p, gr_date: data.invoice_date || p.gr_date, delivery_number: data.shipment_number || p.delivery_number, vehicle_number: v ? v.vehicle_number : vn, owner_name: v ? v.owner_name : p.owner_name, vehicle_type: v ? v.vehicle_type : p.vehicle_type, delivery_location: data.delivery_name || p.delivery_location, quantity: data.quantity ? String(data.quantity) : p.quantity };
      });
      toast.success("Invoice read — review & save");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read the invoice"); }
    finally { setOcr(false); }
  };

  const save = async () => {
    if (!f.vehicle_number) return toast.error("Select a vehicle");
    if (!f.delivery_location) return toast.error("Delivery location required");
    setSaving(true);
    try {
      await api.post("/challans", {
        ...f, hub_id: hub, quantity: Number(f.quantity) || 0, company_rate: Number(f.company_rate) || 0,
        market_rate_enabled: false, market_rate: 0, diesel: Number(f.diesel) || 0, cash: 0,
        bank_transfer: Number(f.bank_transfer) || 0, fastag: 0, fuel_qty: Number(f.fuel_qty) || 0, unloading_amount: Number(f.unloading_amount) || 0,
      });
      toast.success("Challan created");
      setF(empty); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save challan"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 pb-4">
      <h2 className="text-lg font-bold">Create Challan</h2>
      {isDalmia && <ScanBox testid="m-challan-scan" title="Scan / Photograph Invoice" sub="Auto-fills date, shipment, vehicle, qty" loading={ocr} onFile={scan} />}
      <Field label="Date"><Input type="date" value={f.gr_date} onChange={(e) => setF({ ...f, gr_date: e.target.value })} data-testid="m-challan-date" /></Field>
      <Field label="Vehicle">
        <Select value={f.vehicle_number} onValueChange={pickVehicle}>
          <SelectTrigger data-testid="m-challan-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
          <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.vehicle_number}>{v.vehicle_number}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Owner"><Input value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} placeholder="Auto from vehicle" data-testid="m-challan-owner" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Material">
          <Select value={f.material_type} onValueChange={(v) => setF({ ...f, material_type: v })}>
            <SelectTrigger data-testid="m-challan-material"><SelectValue /></SelectTrigger>
            <SelectContent>{(materials.length ? materials : ["Cement"]).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Load Location">
          <Select value={f.load_location} onValueChange={(v) => setF({ ...f, load_location: v })}>
            <SelectTrigger data-testid="m-challan-load"><SelectValue placeholder="Load" /></SelectTrigger>
            <SelectContent>{loadLocs.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Delivery Location"><Input list="m-delloc" value={f.delivery_location} onChange={(e) => setF({ ...f, delivery_location: e.target.value })} data-testid="m-challan-delivery" />
        <datalist id="m-delloc">{delLocs.map((d) => <option key={d} value={d} />)}</datalist>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Delivery / Shipment No"><Input value={f.delivery_number} onChange={(e) => setF({ ...f, delivery_number: e.target.value })} data-testid="m-challan-dno" /></Field>
        <Field label="Quantity (Tons)"><Input type="number" inputMode="decimal" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="m-challan-qty" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Company Rate (₹/T)"><Input type="number" inputMode="decimal" value={f.company_rate} onChange={(e) => setF({ ...f, company_rate: e.target.value })} data-testid="m-challan-rate" /></Field>
        <Field label="Diesel (₹)"><Input type="number" inputMode="decimal" value={f.diesel} onChange={(e) => setF({ ...f, diesel: e.target.value })} data-testid="m-challan-diesel" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Bank Transfer (₹)"><Input type="number" inputMode="decimal" value={f.bank_transfer} onChange={(e) => setF({ ...f, bank_transfer: e.target.value })} data-testid="m-challan-bank" /></Field>
        {isDalmia && <Field label="Fuel Qty (L)"><Input type="number" inputMode="decimal" value={f.fuel_qty} onChange={(e) => setF({ ...f, fuel_qty: e.target.value })} data-testid="m-challan-fuelqty" /></Field>}
      </div>
      {isDalmia && <Field label="Unloading Amount (₹)"><Input type="number" inputMode="decimal" value={f.unloading_amount} onChange={(e) => setF({ ...f, unloading_amount: e.target.value })} data-testid="m-challan-unloading" /></Field>}
      <Button className="w-full h-12 text-base" onClick={save} disabled={saving} data-testid="m-challan-save">{saving ? "Saving…" : "Save Challan"}</Button>
    </div>
  );
}

function ExpenseScreen({ hub, onDone }) {
  const empty = { expense_date: today(), category: "Diesel", vehicle_number: "", narration: "", amount: "", gst_enabled: false, gst_amount: "", diesel_volume: "", diesel_rate: "", location: "" };
  const [f, setF] = useState(empty);
  const [ocr, setOcr] = useState(false);
  const [saving, setSaving] = useState(false);

  const scan = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setOcr(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/expenses/ocr`, fd, { headers: authHeaders() });
      setF((p) => ({ ...p, category: data.category || p.category, amount: data.amount ? String(data.amount) : p.amount, gst_enabled: data.gst_enabled || p.gst_enabled, gst_amount: data.gst_amount ? String(data.gst_amount) : p.gst_amount, narration: data.narration || p.narration, expense_date: data.expense_date || p.expense_date, diesel_volume: data.diesel_volume ? String(data.diesel_volume) : p.diesel_volume, diesel_rate: data.diesel_rate ? String(data.diesel_rate) : p.diesel_rate, location: data.location || p.location }));
      toast.success("Bill read — review & save");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read the bill"); }
    finally { setOcr(false); }
  };

  const save = async () => {
    if (!f.amount) return toast.error("Amount required");
    setSaving(true);
    try {
      await api.post("/expenses", { ...f, amount: Number(f.amount) || 0, gst_amount: Number(f.gst_amount) || 0, diesel_volume: Number(f.diesel_volume) || 0, diesel_rate: Number(f.diesel_rate) || 0, hub_id: hub });
      toast.success("Expense recorded");
      setF(empty); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save expense"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 pb-4">
      <h2 className="text-lg font-bold">Record Expenditure</h2>
      <ScanBox testid="m-expense-scan" title="Scan / Photograph Bill" sub="Auto-fills amount, GST, diesel details" loading={ocr} onFile={scan} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><Input type="date" value={f.expense_date} onChange={(e) => setF({ ...f, expense_date: e.target.value })} data-testid="m-expense-date" /></Field>
        <Field label="Category">
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger data-testid="m-expense-category"><SelectValue /></SelectTrigger>
            <SelectContent>{["Diesel", "Repair", "Tyre", "Toll", "Insurance", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Amount (₹)"><Input type="number" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="m-expense-amount" /></Field>
      <Field label="Narration"><Input value={f.narration} onChange={(e) => setF({ ...f, narration: e.target.value })} data-testid="m-expense-narration" /></Field>
      <div className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
        <Switch checked={f.gst_enabled} onCheckedChange={(v) => setF({ ...f, gst_enabled: v })} data-testid="m-expense-gst" />
        <span className="text-sm font-medium">GST</span>
        {f.gst_enabled && <Input type="number" inputMode="decimal" className="ml-auto w-32" placeholder="GST ₹" value={f.gst_amount} onChange={(e) => setF({ ...f, gst_amount: e.target.value })} data-testid="m-expense-gst-amt" />}
      </div>
      {f.category === "Diesel" && (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Litres"><Input type="number" inputMode="decimal" value={f.diesel_volume} onChange={(e) => setF({ ...f, diesel_volume: e.target.value })} data-testid="m-expense-litres" /></Field>
          <Field label="₹/L"><Input type="number" inputMode="decimal" value={f.diesel_rate} onChange={(e) => setF({ ...f, diesel_rate: e.target.value })} data-testid="m-expense-rate" /></Field>
          <Field label="Location"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="m-expense-loc" /></Field>
        </div>
      )}
      <Button className="w-full h-12 text-base" onClick={save} disabled={saving} data-testid="m-expense-save">{saving ? "Saving…" : "Save Expense"}</Button>
    </div>
  );
}

function FuelScreen({ challans, onDone }) {
  const [sel, setSel] = useState("");
  const [fuelQty, setFuelQty] = useState("");
  const [amount, setAmount] = useState("");
  const [ocr, setOcr] = useState(false);
  const [saving, setSaving] = useState(false);
  const row = challans.find((c) => c.id === sel);

  const scan = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setOcr(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/challans/fuel-ocr`, fd, { headers: authHeaders() });
      if (data.fuel_qty) setFuelQty(String(data.fuel_qty));
      if (data.amount) setAmount(String(data.amount));
      toast.success("Fuel receipt read — review & save");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read the receipt"); }
    finally { setOcr(false); }
  };

  const save = async () => {
    if (!row) return toast.error("Select a dispatch");
    setSaving(true);
    try {
      await api.put(`/challans/${row.id}`, { ...row, fuel_qty: Number(fuelQty) || 0, diesel: Number(amount) || 0 });
      toast.success("Fuel recorded");
      setSel(""); setFuelQty(""); setAmount(""); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save fuel"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 pb-4">
      <h2 className="text-lg font-bold">Record Fuel</h2>
      <Field label="Dispatch">
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger data-testid="m-fuel-challan"><SelectValue placeholder="Select a recent dispatch" /></SelectTrigger>
          <SelectContent>{challans.slice(0, 40).map((c) => <SelectItem key={c.id} value={c.id}>{c.vehicle_number} · {c.delivery_number || c.gr_no} · {fmtDate(c.gr_date)}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <ScanBox testid="m-fuel-scan" title="Scan / Photograph Fuel Receipt" sub="Auto-fills litres & amount" loading={ocr} onFile={scan} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fuel Qty (L)"><Input type="number" inputMode="decimal" value={fuelQty} onChange={(e) => setFuelQty(e.target.value)} data-testid="m-fuel-qty" /></Field>
        <Field label="Diesel Amount (₹)"><Input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="m-fuel-amount" /></Field>
      </div>
      <Button className="w-full h-12 text-base" onClick={save} disabled={saving} data-testid="m-fuel-save">{saving ? "Saving…" : "Save Fuel"}</Button>
    </div>
  );
}

function HomeScreen({ challans, expenses, hubName, go }) {
  const recent = [
    ...challans.slice(0, 6).map((c) => ({ id: "c" + c.id, kind: "Challan", title: `${c.vehicle_number} · ${c.delivery_number || c.gr_no}`, date: c.gr_date, amt: (c.quantity || 0) * (c.company_rate || 0) })),
    ...expenses.slice(0, 6).map((e) => ({ id: "e" + e.id, kind: e.category || "Expense", title: e.narration || "Expense", date: e.expense_date, amt: e.amount })),
  ].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 8);

  const tiles = [
    { label: "New Challan", icon: Send, tab: "challan", tone: "bg-primary text-primary-foreground" },
    { label: "Expenditure", icon: Wallet, tab: "expense", tone: "bg-rose-500 text-white" },
    { label: "Record Fuel", icon: Fuel, tab: "fuel", tone: "bg-amber-500 text-white" },
  ];

  return (
    <div className="space-y-5 pb-4">
      <div className="grid grid-cols-3 gap-3">
        {tiles.map((t) => (
          <button key={t.tab} onClick={() => go(t.tab)} data-testid={`m-tile-${t.tab}`} className="rounded-2xl border border-border bg-card p-4 flex flex-col items-center gap-2 active:scale-95 transition-transform">
            <span className={`h-11 w-11 rounded-xl ${t.tone} flex items-center justify-center`}><t.icon className="h-5 w-5" /></span>
            <span className="text-xs font-semibold text-center leading-tight">{t.label}</span>
          </button>
        ))}
      </div>
      <div>
        <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wide mb-2">My Recent Entries</h3>
        <div className="space-y-2" data-testid="m-recent-list">
          {recent.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">No entries yet in {hubName}</div>}
          {recent.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
              <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${r.kind === "Challan" ? "bg-primary/10 text-primary" : "bg-rose-500/10 text-rose-500"}`}>
                {r.kind === "Challan" ? <Truck className="h-4 w-4" /> : <Wallet className="h-4 w-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.title}</div>
                <div className="text-xs text-muted-foreground">{r.kind} · {fmtDate(r.date)}</div>
              </div>
              <div className="text-sm font-mono font-semibold">{fmtCurrency(r.amt)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MobileApp() {
  const { company, hubs, selectedHub, setSelectedHub, hubName } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("home");
  const [vehicles, setVehicles] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loadLocs, setLoadLocs] = useState([]);
  const [delLocs, setDelLocs] = useState([]);
  const [challans, setChallans] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const isDalmia = (hubs.find((h) => h.id === selectedHub)?.name || "").trim().toLowerCase() === "dalmia";

  const loadData = useCallback(async () => {
    if (!selectedHub) return;
    const p = { params: { hub: selectedHub } };
    const [v, m, ll, dl, ch, ex] = await Promise.all([
      api.get("/vehicles", p), api.get("/master/material", p), api.get("/master/load_location", p),
      api.get("/master/delivery_location", p), api.get("/challans", p), api.get("/expenses", p),
    ]);
    setVehicles(v.data);
    setMaterials(m.data.map((x) => x.name));
    setLoadLocs(ll.data.map((x) => x.name));
    setDelLocs(dl.data.map((x) => x.name));
    setChallans([...ch.data].sort((a, b) => (b.gr_date || "").localeCompare(a.gr_date || "")));
    setExpenses([...ex.data].sort((a, b) => (b.expense_date || "").localeCompare(a.expense_date || "")));
  }, [selectedHub]);

  useEffect(() => { loadData(); }, [loadData]);

  const nav = [
    { id: "home", label: "Home", icon: Home },
    { id: "challan", label: "Challan", icon: Send },
    { id: "expense", label: "Expense", icon: Wallet },
    { id: "fuel", label: "Fuel", icon: Fuel },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="sticky top-0 z-20 flex items-center gap-2 px-4 h-14 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="h-8 w-8 rounded-lg bg-white flex items-center justify-center overflow-hidden p-0.5 shrink-0">
          <img src={company?.logo || "/logo.png"} alt="logo" className="max-h-full max-w-full object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold truncate leading-tight">{company?.name || "TruckLink"}</div>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="m-hub-select" className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {hubName(selectedHub)} <ChevronDown className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {hubs.map((h) => <DropdownMenuItem key={h.id} data-testid={`m-hub-${h.code}`} onClick={() => setSelectedHub(h.id)}>{h.name}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <button onClick={() => navigate("/")} data-testid="m-desktop" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center" title="Desktop view"><Monitor className="h-4 w-4" /></button>
        <button onClick={logout} data-testid="m-logout" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center text-rose-500"><LogOut className="h-4 w-4" /></button>
      </header>

      <main className="flex-1 px-4 py-4 overflow-y-auto">
        {tab === "home" && <HomeScreen challans={challans} expenses={expenses} hubName={hubName(selectedHub)} go={setTab} />}
        {tab === "challan" && <ChallanScreen isDalmia={isDalmia} vehicles={vehicles} materials={materials} loadLocs={loadLocs} delLocs={delLocs} hub={selectedHub} onDone={() => { loadData(); setTab("home"); }} />}
        {tab === "expense" && <ExpenseScreen hub={selectedHub} onDone={() => { loadData(); setTab("home"); }} />}
        {tab === "fuel" && <FuelScreen challans={challans} onDone={() => { loadData(); setTab("home"); }} />}
      </main>

      <nav className="sticky bottom-0 z-20 grid grid-cols-4 border-t border-border bg-background/90 backdrop-blur-xl">
        {nav.map((n) => (
          <button key={n.id} onClick={() => setTab(n.id)} data-testid={`m-nav-${n.id}`} className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${tab === n.id ? "text-primary" : "text-muted-foreground"}`}>
            <n.icon className="h-5 w-5" />{n.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
